import type { IntlShape } from 'react-intl';

import type { DraftPresentation } from '../../contracts/approval-kinds';

export type DraftHeaderKind = 'writing' | 'note' | 'context' | 'document' | 'edit';

export type DraftHeaderSpec = {
  kind: DraftHeaderKind;
  title?: string;
};

const DRAFT_HEADER_TITLE_MAX = 40;

export const truncateDraftHeaderTitle = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.length > DRAFT_HEADER_TITLE_MAX
    ? `${trimmed.slice(0, DRAFT_HEADER_TITLE_MAX - 1)}…`
    : trimmed;
};

/**
 * Pending-card title by destination. Streaming stays "Writing…".
 * Create (`title_key`, no `document_id_key`) vs edit (`document_id_key`)
 * share `session_document` but must not both say "Draft note".
 */
export const resolveDraftHeaderSpec = (
  status: 'streaming_input' | 'pending_approval',
  draft: DraftPresentation | null,
  toolInput: Record<string, unknown>
): DraftHeaderSpec => {
  if (status === 'streaming_input') return { kind: 'writing' };
  if (draft?.target === 'session_context') return { kind: 'context' };
  if (draft?.target === 'session_document') {
    const title = draft.title_key
      ? truncateDraftHeaderTitle(toolInput[draft.title_key])
      : undefined;
    if (draft.document_id_key) return { kind: 'edit', title };
    return { kind: 'document', title };
  }
  return { kind: 'note' };
};

/** Localized card title for a {@link DraftHeaderSpec}. */
export const formatDraftHeader = (intl: IntlShape, spec: DraftHeaderSpec): string => {
  switch (spec.kind) {
    case 'writing':
      return intl.formatMessage({ defaultMessage: 'Writing…', id: 'Kd68snacT9' });
    case 'context':
      return intl.formatMessage({
        defaultMessage: 'Draft context',
        id: 'my6Z13TD5R',
      });
    case 'edit':
      return spec.title
        ? intl.formatMessage(
            { defaultMessage: 'Draft edit · {title}', id: 'xY7xysHEFF' },
            { title: spec.title }
          )
        : intl.formatMessage({
            defaultMessage: 'Draft edit',
            id: '5nRfdqFmd8',
          });
    case 'document':
      return spec.title
        ? intl.formatMessage(
            {
              defaultMessage: 'Draft document · {title}',
              id: 'QprU2LhjG+',
            },
            { title: spec.title }
          )
        : intl.formatMessage({
            defaultMessage: 'Draft document',
            id: 'Wjdr2OREQ8',
          });
    case 'note':
      return intl.formatMessage({
        defaultMessage: 'Draft note',
        id: 'FXXgyJygqi',
      });
  }
};
